import { toast } from 'sonner';

import type { BulkOperationResult } from '@scrambl/shared';

/**
 * One place that turns a bulk result into user-facing feedback.
 *
 * The rule this enforces: NEVER report a count the server did not confirm.
 * The tickets bulk path used to toast `Updated ${ids.length} tickets` off the
 * request, so 3-of-10 succeeding still read as "Updated 10" — the exact failure
 * this envelope exists to prevent. Callers pass the server's result; the counts
 * come from there and nowhere else.
 *
 * Partial success is deliberately TWO toasts: a success for what landed, and a
 * separate, persistent error naming what didn't and why. Folding both into one
 * line makes the failure easy to miss, which is how people end up believing a
 * bulk edit applied to records it never touched.
 */
export function toastBulkResult(
  result: BulkOperationResult,
  options: {
    /** Singular noun, e.g. 'contact'. */
    noun: string;
    /** Plural, when it isn't noun + 's'. */
    nounPlural?: string;
    /** Past-tense verb for the success line, e.g. 'updated', 'restored'. */
    verb?: string;
  },
): void {
  const { noun, nounPlural, verb = 'updated' } = options;
  const plural = nounPlural ?? `${noun}s`;
  const label = (count: number) => (count === 1 ? noun : plural);

  if (result.succeeded > 0) {
    toast.success(
      `${result.succeeded} ${label(result.succeeded)} ${verb}`,
    );
  }

  if (result.failed === 0) return;

  // Reasons repeat heavily in practice (the same missing parent, the same
  // reused code), so group them rather than emitting one toast per record.
  const reasonCounts = new Map<string, number>();
  for (const failure of result.failures) {
    reasonCounts.set(failure.reason, (reasonCounts.get(failure.reason) ?? 0) + 1);
  }

  const lines = Array.from(reasonCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([reason, count]) => (count > 1 ? `${count} × ${reason}` : reason));

  if (reasonCounts.size > 3) {
    lines.push(`…and ${reasonCounts.size - 3} other reasons`);
  }

  toast.error(
    `${result.failed} ${label(result.failed)} couldn't be ${verb}`,
    {
      description: lines.join('\n'),
      // Persistent: a silently auto-dismissed partial failure is the same
      // problem as not reporting it.
      duration: Infinity,
      closeButton: true,
    },
  );
}
